import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, NumberInput, TextInput } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import type { Command } from './Commands';

// Money and operations values (owner 2026-10-04; API routes/admin/tham-so.ts, backend lib/tham_so.js). Each field
// shows the value in force, its default and its range; a field left at its default follows the default.
type Luat = { khoa: string; kieu: 'nguyen' | 'so' | 'chuoi' | 'mang'; min?: number; max?: number; mo?: string };
type Part = {
  hieuLuc: Record<string, unknown>; macDinh: Record<string, unknown>; daLuu: Record<string, unknown>; luat: Luat[];
  meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string }[];
};
type Data = { tien: Part; 'van-hanh': Part; canEditMoney: boolean };
type Loai = 'tien' | 'van-hanh';

const NHAN: Record<string, string> = {
  'gioiHanCuoc.cuaMax': 'Cược tối đa trên một cửa trong một ván (mọi game)',
  'gioiHanCuoc.vanMax': 'Tổng cược tối đa của một người trong một ván (mọi game)',
  'gioiHanCuoc.traMax': 'Tiền nhận về tối đa của một cửa / một lần cược, gồm cả tiền cược và hũ (mọi game trừ Keno, game có trần thắng riêng)',
  'miracle.cuocToiDa': 'Sảnh live (Evolution, Pragmatic, Sexy, DG): cược tối đa một vé',
  'miracle.congToiDa': 'Sảnh live: tiền thắng tối đa một vé (vượt thì giữ lại, không cộng)',
  'miracle.vndMoiKrw': 'Sảnh live: tỷ giá, tiền game cho 1 KRW của sảnh',
  'saba.cuocToiDa': 'SABA thể thao: cược tối đa một vé (tiền game; 10.000.000 = 10.000 điểm trong sảnh)',
  'poker.texas.phe.phanTram': 'Poker Texas: phế (% của pot)',
  'poker.texas.phe.tranLanMuLon': 'Poker Texas: phế tối đa mỗi ván (số lần mù lớn)',
  'poker.omaha.phe.phanTram': 'Poker Omaha: phế (% của pot)',
  'poker.omaha.phe.tranLanMuLon': 'Poker Omaha: phế tối đa mỗi ván (số lần mù lớn)',
  'poker.giai.ve.gia': 'Giải Poker: giá vé',
  'poker.giai.ve.phiNha': 'Giải Poker: phí nhà cái trong giá vé',
  'poker.giai.thuong': 'Giải Poker: thưởng từng vòng, từ vòng 1 tới chung kết (cách nhau bằng dấu phẩy)',
  'taiXiu.thue': 'Tài Xỉu: phí thắng (%)',
  'taiXiu.tyLePhiVaoQuy': 'Tài Xỉu: phần phí vào quỹ nhà cái (0 đến 1)',
  'taiXiuMd5.thue': 'Tài Xỉu MD5: phí thắng (%)',
  'taiXiuMd5.tyLePhiVaoQuy': 'Tài Xỉu MD5: phần phí vào quỹ nhà cái (0 đến 1)',
  'bauCua.thue': 'Bầu Cua: phí thắng (% tiền thắng ròng)',
  'xocDia.thue': 'Xóc Đĩa: phí thắng (% tiền thắng ròng)',
  'chongThua.nguongCuocLon': 'Chống thua: ván có tổng cược từ mức này là ván cược lớn',
  'chongThua.tyLeNhaCaiAnCheDoKho': 'Chống thua (chế độ khó): tỷ lệ nhà cái thắng ván cược lớn (0 đến 1)',
  'chongThua.tyLeThaCuocNho': 'Chống thua (chế độ khó): tỷ lệ để người chơi thắng ván cược nhỏ (0 đến 1)',
  'datLaiQuy.soDuSau': 'Reset quỹ game: số dư mỗi quỹ sau khi bấm Reset quỹ, cũng là số dư đầu của game mới bật quỹ chặn (trang MKT và quỹ). Mức gốc của quỹ không đổi',
  'telegram.tenBot': 'Telegram: tên bot của site (không có @, để trống là tắt bot)',
  'telegram.nhomNap': 'Telegram: nhóm nhận báo lệnh nạp (chat id, để trống là không gửi)',
  'telegram.nhomRut': 'Telegram: nhóm nhận báo lệnh rút (chat id, để trống là không gửi)',
  'telegram.nhomAnToan': 'Telegram: nhóm chủ nhận báo game tự dừng và lỗi thông số (chat id)',
  'usdt.napToiThieu': 'Nạp USDT: tối thiểu một lệnh',
  'usdt.napToiDa': 'Nạp USDT: tối đa một lệnh',
  'poker.texas.mua.toiThieuLanMuLon': 'Poker Texas: mua chip vào bàn tối thiểu (số lần mù lớn)',
  'poker.texas.mua.toiDaLanMuLon': 'Poker Texas: mua chip vào bàn tối đa (số lần mù lớn)',
  'poker.omaha.mua.toiThieuLanMuLon': 'Poker Omaha: mua chip vào bàn tối thiểu (số lần mù lớn)',
  'poker.omaha.mua.toiDaLanMuLon': 'Poker Omaha: mua chip vào bàn tối đa (số lần mù lớn)',
  'vongCuoc.choDuyetSauCuocGiay': 'Chờ duyệt rút sau lần cược trả vòng cược thưởng (giây)',
  'cuocDangGiu.miracleToiDaPhut': 'Cược Live Casino (Miracle) chưa có kết quả quá số phút này thì không còn giữ rút tiền / khuyến mãi',
  'khuyenMaiMau.phanTram': 'Khuyến mãi nạp mới: % thưởng điền sẵn',
  'khuyenMaiMau.toiDa': 'Khuyến mãi nạp mới: thưởng tối đa điền sẵn',
  'khuyenMaiMau.napToiThieu': 'Khuyến mãi nạp mới: nạp tối thiểu điền sẵn',
  'khuyenMaiMau.vongCuoc': 'Khuyến mãi nạp mới: số vòng cược điền sẵn',
  'moiBan.soBotCho': 'Nút Mời (Bi-a, Poker): số bot chờ luôn hiện trong danh sách mời (0 = không có bot)',
  'moiBan.botNhanGiayMin': 'Nút Mời: bot được mời nhận lời sau ít nhất (giây)',
  'moiBan.botNhanGiayMax': 'Nút Mời: bot được mời nhận lời sau nhiều nhất (giây)',
};

const get = (o: Record<string, unknown>, k: string): unknown => k.split('.').reduce<unknown>((x, p) => (x && typeof x === 'object' ? (x as Record<string, unknown>)[p] : undefined), o);
function set(o: Record<string, unknown>, k: string, v: unknown): Record<string, unknown> {
  const [h, ...r] = k.split('.');
  const cur = (o[h!] && typeof o[h!] === 'object' && !Array.isArray(o[h!]) ? o[h!] : {}) as Record<string, unknown>;
  return { ...o, [h!]: r.length ? set(cur, r.join('.'), v) : v };
}
const hien = (v: unknown) => Array.isArray(v) ? v.map((x) => Number(x).toLocaleString('vi-VN')).join(', ') : typeof v === 'number' ? v.toLocaleString('vi-VN') : String(v ?? '');
const khoang = (l: Luat) => l.kieu === 'chuoi' ? (l.mo ?? '') : `từ ${hien(l.min)} đến ${hien(l.max)}`;

type Notice = { kind: 'success' | 'warning'; title: string; subtitle: string } | null;

// notice lives in the page: the section remounts on a new version (key) and would drop it right after a save
function Section({ loai, part, editable, title, lead, notice, setNotice }: { loai: Loai; part: Part; editable: boolean; title: string; lead: string; notice: Notice; setNotice: (n: Notice) => void }) {
  const qc = useQueryClient();
  // draft = the full saved document the page will send (fields not set follow their default)
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const doc = draft ?? part.daLuu;
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>(`/api/admin/tham-so/${loai}`, { noiDung: doc, expect: part.meta.version }),
    onSuccess: () => {
      setDraft(null); qc.invalidateQueries({ queryKey: ['tham-so'] });
      setNotice({ kind: 'success', title: 'Đã lưu', subtitle: 'Máy chủ game dùng giá trị mới từ lệnh cược, ván, bàn hoặc lệnh nạp tiếp theo, không cần khởi động lại.' });
    },
    onError: (e) => { if (e instanceof ApiError && e.code === 'STALE') { setDraft(null); qc.invalidateQueries({ queryKey: ['tham-so'] }); } },
  });
  return (
    <section className="section">
      <h2>{title}</h2>
      <p className="muted">{lead}</p>
      <p className="muted">Phiên bản {part.meta.version} ({part.meta.source === 'cms' ? 'đã lưu trên CMS' : 'tất cả theo mặc định'}){part.meta.at ? <> · <When v={part.meta.at} /></> : null}</p>
      {!editable ? <InlineNotification kind="info" lowContrast hideCloseButton title="Chỉ chủ sở hữu sửa được" subtitle="Đây là giá trị tiền. Bạn xem được, không sửa được." /> : null}
      {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
      <div className="stack" style={{ maxWidth: 640 }}>
        {part.luat.map((l) => {
          const saved = get(doc, l.khoa);
          const v = saved !== undefined ? saved : get(part.hieuLuc, l.khoa);
          const help = `Mặc định ${hien(get(part.macDinh, l.khoa)) || '(trống)'} · ${khoang(l)}${saved === undefined ? ' · đang theo mặc định' : ''}`;
          const id = `ts-${loai}-${l.khoa}`;
          if (l.kieu === 'chuoi' || l.kieu === 'mang') {
            return <TextInput key={l.khoa} id={id} labelText={NHAN[l.khoa] ?? l.khoa} helperText={help} disabled={!editable}
              value={l.kieu === 'mang' ? (Array.isArray(v) ? v.join(', ') : String(v ?? '')) : String(v ?? '')}
              onChange={(e) => setDraft(set(doc, l.khoa, l.kieu === 'mang' ? e.target.value.split(',').map((x) => Number(x.replace(/[^\d]/g, ''))).filter((x) => x > 0) : e.target.value.trim()))} />;
          }
          return <NumberInput key={l.khoa} id={id} label={NHAN[l.khoa] ?? l.khoa} helperText={help} disabled={!editable} min={l.min} max={l.max}
            step={l.kieu === 'so' ? 0.01 : 1} value={Number(v)} allowEmpty={false} onChange={(_e, d) => setDraft(set(doc, l.khoa, Number(d.value)))} />;
        })}
        {editable ? (
          <div className="row">
            <Button size="sm" disabled={!draft || save.isPending} onClick={() => save.mutate()}>Lưu</Button>
            <Button size="sm" kind="ghost" disabled={!draft} onClick={() => setDraft(null)}>Bỏ thay đổi</Button>
          </div>
        ) : null}
        <InlineError error={save.error} />
      </div>
    </section>
  );
}

export function ThamSoPage() {
  const q = useQuery({ queryKey: ['tham-so'], queryFn: () => api.get<Data>('/api/admin/tham-so') });
  const [notice, setNotice] = useState<Record<Loai, Notice>>({ tien: null, 'van-hanh': null });
  const noticeOf = (loai: Loai) => ({ notice: notice[loai], setNotice: (n: Notice) => setNotice((x) => ({ ...x, [loai]: n })) });
  return (
    <div className="page">
      <PageHeader title="Thông số tiền và vận hành"
        lead="Các con số trước đây nằm cứng trong mã. Lưu xong là máy chủ game dùng ngay, không cần cập nhật bản mới." />
      {q.isLoading ? <BlockSkeleton lines={12} /> : q.error || !q.data ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <Section key={'tien' + q.data.tien.meta.version} loai="tien" part={q.data.tien} editable={q.data.canEditMoney} {...noticeOf('tien')} title="Thông số tiền (chỉ chủ sở hữu)"
            lead="Trần cược và trần thắng toàn sàn, sảnh live, phế và giải Poker, phí Tài Xỉu, Bầu Cua, Xóc Đĩa, chống thua, Telegram. Phí thắng chỉ lấy ở đây; RTP mục tiêu (trang RTP game) không đổi phí hay tiền trả." />
          <Section key={'vh' + q.data['van-hanh'].meta.version} loai="van-hanh" part={q.data['van-hanh']} editable {...noticeOf('van-hanh')} title="Thông số vận hành"
            lead="Hạn mức nạp USDT, mua chip Poker, thời gian chờ duyệt rút sau cược, giá trị điền sẵn khi tạo khuyến mãi nạp." />
        </>
      )}
    </div>
  );
}
